import React from 'react';
import { ChevronRight } from 'lucide-react';
import './Breadcrumb.css';

export interface BreadcrumbItem {
  /** Label to display */
  label: string;
  /** Click handler - if provided, item is clickable */
  onClick?: () => void;
}

export interface BreadcrumbProps {
  /** Array of breadcrumb items */
  items: BreadcrumbItem[];
  /** Optional class name */
  className?: string;
}

/**
 * Breadcrumb navigation component.
 * Displays a trail of navigation items separated by chevrons.
 *
 * Example: "Documents > Invoices > 2024"
 * Example: "Deleted documents > Contracts"
 */
export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  className = '',
}) => {
  if (items.length === 0) return null;

  return (
    <nav className={`breadcrumb ${className}`} aria-label="Breadcrumb">
      <ol className="breadcrumb-list">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const isClickable = !!item.onClick && !isLast;

          return (
            <li key={index} className="breadcrumb-item">
              {isClickable ? (
                <button
                  className="breadcrumb-link"
                  onClick={item.onClick}
                  type="button"
                >
                  {item.label}
                </button>
              ) : (
                <span className={`breadcrumb-text ${isLast ? 'breadcrumb-text-current' : ''}`}>
                  {item.label}
                </span>
              )}
              {!isLast && (
                <ChevronRight className="breadcrumb-separator" aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
